import { extractErrorMessage } from "../utils/acceptSuggestions";
import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useTrial } from "../context/TrialContext";
import StepDialog from "../components/StepDialog";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import {
  Users,
  UserCog,
  Shield,
  Building2,
  Copy,
  Check,
  Trash2,
  AlertTriangle,
  FileText,
  ClipboardCheck,
  BookOpen,
  TrendingUp,
  Bell,
  Calendar,
  Download,
  UserPlus,
  Mail,
  Send,
  RefreshCw,
  Clock,
  XCircle,
  CheckCircle2,
  Building,
  UserCheck,
  Plus,
  Target,
  Eye,
  Heart,
  Crown,
  Phone,
  MapPin,
  Edit2,
  Save,
  Sparkles,
  Loader2,
  ChevronDown,
  Home,
} from "lucide-react";
import RoleBasedInviteModal from "../components/RoleBasedInviteModal";
import BulkInviteCSVModal from "../components/BulkInviteCSVModal";
import AISuggestionsBanner from "../components/AISuggestionsBanner";
import { resolveRoleLabel } from "../lib/roleLabel";

const SITE_TYPES = [
  ["headquarters", "Headquarters"], ["branch", "Branch Office"], ["datacenter", "Data Center"],
  ["warehouse", "Warehouse"], ["manufacturing", "Manufacturing Plant"], ["retail", "Retail Store"], ["other", "Other"],
];
const SITE_ADDR = [["street", "Street"], ["city", "City"], ["state", "State / Province"], ["postal_code", "Postal code"], ["country", "Country"]];
const EMPTY_SITE = { name: "", type: "branch", function: "", phone: "", street: "", city: "", state: "", postal_code: "", country: "" };

function SiteDialog({ site, sites = [], onClose, onSaved, api }) {
  const [form, setForm] = useState(EMPTY_SITE);
  const [saving, setSaving] = useState(false);
  const [dupMsg, setDupMsg] = useState("");
  useEffect(() => {
    setDupMsg("");
    if (site) setForm({ ...EMPTY_SITE, ...Object.fromEntries(Object.keys(EMPTY_SITE).map((k) => [k, site[k] ?? EMPTY_SITE[k]])) });
  }, [site]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    if (!form.name.trim()) return toast.error("Site name is required");
    const hasAddr = ["street", "city", "state", "postal_code"].some((k) => form[k].trim());
    if (hasAddr && !form.country.trim()) return toast.error("Country is required when an address is entered");
    const nm = form.name.trim();
    const dup = sites.find((l) => l.id !== site?.id && (l.name || "").trim().toLowerCase() === nm.toLowerCase());
    if (dup) {
      const msg = dup.status === "retired" ? `Site ${nm} is retired. Reactivate it on Organization Management.` : `A site named ${nm} already exists - use Edit`;
      setDupMsg(msg);
      return toast.error(msg);
    }
    setDupMsg("");
    setSaving(true);
    try {
      if (site?.id) await api.put(`/bcm-admin/locations/${site.id}`, form);
      else await api.post("/bcm-admin/locations", form);
      toast.success(site?.id ? "Site updated" : "Site added");
      onSaved();
    } catch (err) {
      if (err?.response?.status === 409 && typeof err.response.data?.detail === "string") setDupMsg(err.response.data.detail);
      toast.error(extractErrorMessage(err, "Could not save site"));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={!!site} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="site-dialog">
        <DialogHeader>
          <DialogTitle>{site?.id ? "Edit site" : "Add site"}</DialogTitle>
          <DialogDescription>Sites are shared by departments, executives, Asset Inventory and Risk Assessment.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1"><Label>Site name *</Label><Input value={form.name} onChange={set("name")} data-testid="site-name-input" /></div>
          <div className="space-y-1"><Label>Type</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="site-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{SITE_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1"><Label>Function</Label><Input value={form.function} onChange={set("function")} data-testid="site-function-input" /></div>
          {SITE_ADDR.map(([k, l]) => (
            <div key={k} className={k === "street" ? "col-span-2 space-y-1" : "space-y-1"}>
              <Label>{l}{k === "country" ? " (required if address entered)" : ""}</Label>
              <Input value={form[k]} onChange={set(k)} data-testid={`site-${k}-input`} />
            </div>
          ))}
          <div className="space-y-1"><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} data-testid="site-phone-input" /></div>
        </div>
        {dupMsg && <p className="text-sm text-red-600" data-testid="site-duplicate-msg">{dupMsg}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} data-testid="site-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="site-save-btn">{saving ? "Saving…" : "Save site"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const todayISO = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

// S2 FU2 — one source of truth: the server's delete-guard reference list (read-only).
async function siteLinks(api, loc) {
  const r = await api.get(`/bcm-admin/locations/${loc.id}/references`);
  return r.data?.used_by || [];
}

function RetireSiteDialog({ site, sites, links, api, onClose, onSaved }) {
  const [f, setF] = useState({ effective_date: todayISO(), reason: "", moved_to_location_id: "" });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setF({ effective_date: todayISO(), reason: "", moved_to_location_id: "" }); setErr(""); }, [site]);
  const save = async () => {
    if (!f.reason.trim()) return setErr("A reason is required to retire a site.");
    setSaving(true);
    try {
      await api.put(`/bcm-admin/locations/${site.id}/status`, { status: "retired", ...f, moved_to_location_id: f.moved_to_location_id || null });
      toast.success("Site retired");
      onSaved();
    } catch (e) {
      setErr(extractErrorMessage(e, "Could not retire site"));
    } finally { setSaving(false); }
  };
  const targets = sites.filter((l) => l.id !== site?.id && l.status !== "retired");
  return (
    <Dialog open={!!site} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="retire-site-dialog">
        <DialogHeader>
          <DialogTitle>Retire site {site?.name}</DialogTitle>
          <DialogDescription>The site and its history are kept; it is no longer offered for new choices.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label>Effective date *</Label><Input type="date" value={f.effective_date} onChange={(e) => setF({ ...f, effective_date: e.target.value })} data-testid="retire-date-input" /></div>
          <div className="space-y-1"><Label>Reason *</Label><Textarea value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="retire-reason-input" /></div>
          <div className="space-y-1"><Label>Operations moved to (optional)</Label>
            <Select value={f.moved_to_location_id || "none"} onValueChange={(v) => setF({ ...f, moved_to_location_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="retire-moved-to-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Not moved —</SelectItem>
                {targets.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div className="rounded border bg-slate-50 p-2 text-sm" data-testid="retire-linked-list">
            <p className="font-medium mb-1">Still linked to this site</p>
            {links === null ? <p className="text-slate-500">Checking…</p> : links === false ? <p className="text-red-600" data-testid="retire-links-error">Could not load links</p> : links.length === 0 ? <p className="text-slate-500">Nothing is linked.</p>
              : <ul className="list-disc pl-5">{links.map((x, i) => <li key={i}>{x}</li>)}</ul>}
            <p className="text-xs text-slate-500 mt-1">These links are kept as they are.</p>
          </div>
          {err && <p className="text-sm text-red-600" data-testid="retire-error">{err}</p>}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} data-testid="retire-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="retire-save-btn">{saving ? "Retiring…" : "Retire site"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RetiredSites({ retired, sites, canManage, onReactivate }) {
  if (retired.length === 0) return null;
  const nameOf = (id) => sites.find((l) => l.id === id)?.name || "—";
  return (
    <div className="mt-6" data-testid="retired-sites-section">
      <p className="font-medium text-slate-700 mb-2">Retired sites</p>
      <table className="w-full text-sm" data-testid="retired-sites-table">
        <thead><tr className="border-b text-left"><th className="py-2 px-3">Site</th><th className="py-2 px-3">Effective</th><th className="py-2 px-3">Reason</th><th className="py-2 px-3">Moved to</th><th className="py-2 px-3">Retired by</th>{canManage && <th className="py-2 px-3" />}</tr></thead>
        <tbody>{retired.map((l) => { const r = l.retirement || {}; return (
          <tr key={l.id} className="border-b last:border-0 text-slate-600" data-testid={`retired-site-row-${l.id}`}>
            <td className="py-2 px-3 font-medium">{l.name}</td><td className="py-2 px-3">{r.effective_date || "—"}</td>
            <td className="py-2 px-3">{r.reason || "—"}</td><td className="py-2 px-3">{r.moved_to_location_id ? nameOf(r.moved_to_location_id) : "—"}</td>
            <td className="py-2 px-3">{r.retired_by_name || "—"}</td>
            {canManage && <td className="py-2 px-3 text-right"><Button variant="outline" size="sm" onClick={() => onReactivate(l)} data-testid={`reactivate-site-${l.id}`}>Reactivate</Button></td>}
          </tr>); })}</tbody>
      </table>
    </div>
  );
}

function SitesCard({ locations, canManage, api, onChanged }) {
  const [editing, setEditing] = useState(null);
  const [blocked, setBlocked] = useState("");
  const [retiring, setRetiring] = useState(null);
  const [links, setLinks] = useState(null);
  const active = locations.filter((l) => l.status !== "retired");
  const retired = locations.filter((l) => l.status === "retired");
  const openRetire = async (loc) => {
    setRetiring(loc); setLinks(null);
    try { setLinks(await siteLinks(api, loc)); } catch { setLinks(false); }
  };
  const reactivate = async (loc) => {
    try {
      await api.put(`/bcm-admin/locations/${loc.id}/status`, { status: "active" });
      toast.success("Site reactivated");
      onChanged();
    } catch (err) { toast.error(extractErrorMessage(err, "Could not reactivate site")); }
  };
  const remove = async (loc) => {
    setBlocked("");
    if (!window.confirm(`Delete site "${loc.name}"?`)) return;
    try {
      await api.delete(`/bcm-admin/locations/${loc.id}`);
      toast.success("Site deleted");
      onChanged();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 409 && d?.used_by) setBlocked(`This site is still used by: ${d.used_by.join("; ")}. Move or remove these first, or Retire it instead.`);
      else toast.error(extractErrorMessage(err, "Could not delete site"));
    }
  };
  return (
    <Card data-testid="sites-card">
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="flex items-center gap-2"><MapPin className="w-5 h-5 text-sky-700" />Sites</CardTitle>
            <CardDescription>{`${active.length} active site${active.length === 1 ? "" : "s"}${retired.length ? `, ${retired.length} retired` : ""} for this organization`}{canManage ? "" : " · read-only"}</CardDescription>
          </div>
          {canManage && <Button size="sm" onClick={() => setEditing({})} data-testid="add-site-btn"><Plus className="w-4 h-4 mr-1" />Add site</Button>}
        </div>
      </CardHeader>
      <CardContent>
        {blocked && <p className="mb-3 rounded border border-amber-300 bg-amber-50 p-2 text-sm text-amber-800" data-testid="site-delete-blocked">{blocked}</p>}
        {active.length === 0 ? <p className="text-slate-500 text-center py-4">No active sites.</p> : (
          <table className="w-full text-sm" data-testid="sites-table">
            <thead><tr className="border-b"><th className="text-left py-2 px-3">Site</th><th className="text-left py-2 px-3">Type</th><th className="text-left py-2 px-3">Address</th>{canManage && <th className="py-2 px-3" />}</tr></thead>
            <tbody>{active.map((l) => (
              <tr key={l.id} className="border-b last:border-0" data-testid={`site-row-${l.id}`}>
                <td className="py-2 px-3 font-medium">{l.name}</td>
                <td className="py-2 px-3">{(SITE_TYPES.find(([v]) => v === l.type) || [null, l.type])[1]}</td>
                <td className="py-2 px-3 text-slate-600">{l.address || "—"}</td>
                {canManage && <td className="py-2 px-3 text-right whitespace-nowrap">
                  <Button variant="ghost" size="sm" onClick={() => setEditing(l)} data-testid={`edit-site-${l.id}`}><Edit2 className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => openRetire(l)} data-testid={`retire-site-${l.id}`}>Retire</Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(l)} data-testid={`delete-site-${l.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </td>}
              </tr>
            ))}</tbody>
          </table>
        )}
        <RetiredSites retired={retired} sites={locations} canManage={canManage} onReactivate={reactivate} />
      </CardContent>
      <SiteDialog site={editing} sites={locations} api={api} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onChanged(); }} />
      <RetireSiteDialog site={retiring} sites={locations} links={links} api={api} onClose={() => setRetiring(null)} onSaved={() => { setRetiring(null); onChanged(); }} />
    </Card>
  );
}


export default function OrgManagementPage() {
  const { api, user } = useAuth();
  const [deptEdits, setDeptEdits] = useState({});
  const [savingDeptFor, setSavingDeptFor] = useState(null);
  const { triggerUpgradeWall, completeChecklistStep, fetchTrialStatus } = useTrial();
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  const activeLocations = locations.filter((l) => l.status !== "retired");
  const [executives, setExecutives] = useState([]);
  const [stats, setStats] = useState(null);
  const [org, setOrg] = useState(null);
  const [reminders, setReminders] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [codeCopied, setCodeCopied] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);
  const [selectedRole, setSelectedRole] = useState("");
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteStep, setInviteStep] = useState(0);
  const [showDeptDialog, setShowDeptDialog] = useState(false);
  const [showExecDialog, setShowExecDialog] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [editingExec, setEditingExec] = useState(null);
  const [aiSuggestedDepts, setAiSuggestedDepts] = useState([]);
  const [aiSuggestLoading, setAiSuggestLoading] = useState(false);
  // V22.9 inline hierarchical invite (Department Head pre-selected for the row)
  const [inlineInvite, setInlineInvite] = useState({ open: false, dept: null });
  const [showBulkInviteModal, setShowBulkInviteModal] = useState(false);
  
  // Organization Foundation state
  const [orgFoundation, setOrgFoundation] = useState({
    mission_statement: "",
    vision: "",
    core_values: []
  });
  const [newCoreValue, setNewCoreValue] = useState("");
  const [editingFoundation, setEditingFoundation] = useState(false);
  
  const [deptForm, setDeptForm] = useState({
    name: "",
    description: "",
    criticality: "medium",
    location_ids: [],
    work_from_home: false,
    primary_owner_id: "",
    secondary_owner_id: "",
    primary_owner_mode: "existing",
    secondary_owner_mode: "existing",
    primary_new: { first_name: "", last_name: "", email: "" },
    secondary_new: { first_name: "", last_name: "", email: "" },
    primary_contact: "",
    secondary_contact: "",
    primary_owner_phone: "",
    primary_owner_work_location: "",
    secondary_owner_phone: "",
    secondary_owner_work_location: "",
    primary_owner_home: { home_street: "", home_city: "", home_state: "", home_postal_code: "", home_country: "" },
    secondary_owner_home: { home_street: "", home_city: "", home_state: "", home_postal_code: "", home_country: "" }
  });
  const [deptError, setDeptError] = useState("");
  const [invitingHeads, setInvitingHeads] = useState(false);
  
  const [execForm, setExecForm] = useState({
    name: "",
    designation: "",
    role_type: "executive", // executive, decision_maker, sponsor, or custom
    email: "",
    phone: "",
    notes: "",
    location_id: ""
  });
  
  const [inviteForm, setInviteForm] = useState({
    email: "",
    first_name: "",
    last_name: "",
    department: ""
  });
  const [inviting, setInviting] = useState(false);
  const [invitingOwner, setInvitingOwner] = useState(null);
  const [showITDRPInviteDialog, setShowITDRPInviteDialog] = useState(false);
  const [itdrpForm, setItdrpForm] = useState({ email: "", first_name: "", last_name: "" });
  const [itdrpInviting, setItdrpInviting] = useState(false);

  // V14 — BCM-only special-invite whitelist (executive sponsor / auditor / deputy)
  const [showBcmExceptionDialog, setShowBcmExceptionDialog] = useState(false);
  const [bcmExceptionForm, setBcmExceptionForm] = useState({
    email: "",
    first_name: "",
    last_name: "",
    role: "executive_sponsor",
  });
  const [bcmExceptionInviting, setBcmExceptionInviting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const isUserHead = (u) => departments.some(
    (d) => d.primary_owner_id === u.id || d.secondary_owner_id === u.id || d.team_lead_id === u.id
  );
  const myDepartment = () => departments.find(
    (d) => d.primary_owner_id === user?.id || d.secondary_owner_id === user?.id
  );
  const canSetUserDept = (u) => {
    if (isUserHead(u)) return false;
    if (user?.role === "bcm_manager" || user?.role === "admin") return true;
    if (user?.role === "department_head") return !u.department && !!myDepartment();
    return false;
  };
  const deptOptionsFor = (u) => {
    if (user?.role === "department_head") {
      const md = myDepartment();
      return md ? [md] : [];
    }
    return departments;
  };
  const saveUserDepartment = async (u) => {
    const deptId = deptEdits[u.id];
    if (!deptId) return;
    setSavingDeptFor(u.id);
    try {
      const res = await api.put(`/bcm-admin/users/${u.id}/department`, { department_id: deptId });
      toast.success(res.data?.message || "Department updated");
      setDeptEdits((prev) => { const n = { ...prev }; delete n[u.id]; return n; });
      await fetchData();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to set department"));
    } finally {
      setSavingDeptFor(null);
    }
  };


  const fetchData = async () => {
    try {
      const [usersRes, statsRes, orgRes, invitationsRes, deptsRes, locsRes] = await Promise.all([
        api.get("/bcm-admin/users"),
        api.get("/bcm-admin/dashboard/stats"),
        api.get("/bcm-admin/organization"),
        api.get("/auth/invitations"),
        api.get("/bcm-admin/departments"),
        api.get("/bcm-admin/locations"),
      ]);
      // Handle users - API returns {users: [...]}
      const usersData = usersRes.data?.users || usersRes.data || [];
      setUsers(Array.isArray(usersData) ? usersData : []);
      setStats(statsRes.data || {});
      setOrg(orgRes.data || {});
      setInvitations(invitationsRes.data?.invitations || invitationsRes.data || []);
      setDepartments(deptsRes.data || []);
      setLocations(locsRes.data || []);
      
      // Load organization foundation (Mission/Vision/Values)
      if (orgRes.data) {
        setOrgFoundation({
          mission_statement: orgRes.data.mission_statement || "",
          vision: orgRes.data.vision || "",
          core_values: orgRes.data.core_values || []
        });
      }
      
      // Load executives
      try {
        const execRes = await api.get("/bcm-admin/executives");
        setExecutives(execRes.data || []);
      } catch (e) {
        // Executives endpoint may not exist yet
        setExecutives([]);
      }
      
      // Try to load reminders separately (non-critical)
      try {
        const remindersRes = await api.get("/exercises/reminders");
        const data = remindersRes.data || {};
        // Transform to expected format if needed
        if (data.reminders && !data.summary) {
          const overdueCount = data.reminders.filter(r => r.urgency === 'overdue').length;
          const urgentCount = data.reminders.filter(r => ['today', 'this_week'].includes(r.urgency)).length;
          setReminders({
            reminders: data.reminders,
            summary: {
              overdue: overdueCount,
              urgent: urgentCount,
              total: data.reminders.length
            }
          });
        } else {
          setReminders(data);
        }
      } catch (e) {
        setReminders(null);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      // S1 FU1: the site list is readable by every role even if other panels are not
      api.get("/bcm-admin/locations").then((r) => setLocations(r.data || [])).catch(() => {});
      toast.error("Failed to load organization data");
    } finally {
      setLoading(false);
    }
  };

  const copyOrgCode = async () => {
    if (org?.code) {
      try {
        await navigator.clipboard.writeText(org.code);
        setCodeCopied(true);
        toast.success("Organization code copied!");
        setTimeout(() => setCodeCopied(false), 2000);
      } catch (err) {
        toast.error("Failed to copy code");
      }
    }
  };

  const confirmDeleteUser = (userObj) => {
    setUserToDelete(userObj);
    setShowDeleteDialog(true);
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      await api.delete(`/bcm-admin/users/${userToDelete.id}`);
      toast.success("User removed from organization");
      setShowDeleteDialog(false);
      setUserToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to remove user"));
    }
  };

  const openRoleDialog = (userObj) => {
    setUserToEdit(userObj);
    setSelectedRole(userObj.role);
    setShowRoleDialog(true);
  };

  const handleUpdateRole = async () => {
    if (!userToEdit || !selectedRole) return;
    
    try {
      await api.put(`/bcm-admin/users/${userToEdit.id}/role?role=${selectedRole}`);
      toast.success("User role updated");
      setShowRoleDialog(false);
      setUserToEdit(null);
      fetchData();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to update role"));
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    
    try {
      await api.post("/auth/invite", inviteForm);
      toast.success(`Invitation sent to ${inviteForm.email}`);
      setShowInviteDialog(false);
      setInviteForm({ email: "", first_name: "", last_name: "", department: "" });
      fetchData();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to send invitation"));
    } finally {
      setInviting(false);
    }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      await api.delete(`/auth/invitations/${inviteId}`);
      toast.success("Invitation cancelled");
      fetchData();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to cancel invitation"));
    }
  };

  const handleResendInvite = async (inviteId) => {
    try {
      const res = await api.post(`/auth/invitations/${inviteId}/resend`);
      if (res.data?.email_sent) {
        toast.success("Invitation email resent");
      } else {
        const url = res.data?.accept_url
          ? (res.data.accept_url.startsWith("http") ? res.data.accept_url : `${window.location.origin}${res.data.accept_url}`)
          : null;
        if (url) { try { await navigator.clipboard.writeText(url); } catch { /* clipboard may be blocked */ } }
        toast.warning("Invitation updated, but the email failed to send", {
          description: (res.data?.delivery_note || "The recipient was not emailed.") +
            (url ? " Activation link copied to clipboard for manual delivery." : ""),
          duration: 10000,
        });
      }
      fetchData();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to resend invitation"));
    }
  };

  const handleInviteDeptOwner = async (deptId, ownerType) => {
    setInvitingOwner(`${deptId}-${ownerType}`);
    try {
      const res = await api.post(`/bcm-admin/departments/${deptId}/invite-owner`, { owner_type: ownerType });
      toast.success(res.data?.message || "Department Head invitation sent!");
      fetchData();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to send Department Head invitation"));
    } finally {
      setInvitingOwner(null);
    }
  };

  const handleInviteITDRP = async (e) => {
    e.preventDefault();
    setItdrpInviting(true);
    try {
      const res = await api.post("/bcm-admin/invite-itdrp-manager", itdrpForm);
      toast.success(res.data?.message || "IT-DRP Manager invitation sent!");
      setShowITDRPInviteDialog(false);
      setItdrpForm({ email: "", first_name: "", last_name: "" });
      fetchData();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to send IT-DRP Manager invitation"));
    } finally {
      setItdrpInviting(false);
    }
  };

  // V14 — BCM-only special invite (executive sponsor / auditor / deputy BCM)
  const handleBcmExceptionInvite = async (e) => {
    e.preventDefault();
    setBcmExceptionInviting(true);
    try {
      const res = await api.post("/auth/invite", bcmExceptionForm);
      toast.success(res.data?.message || `Invitation sent to ${bcmExceptionForm.email}`);
      setShowBcmExceptionDialog(false);
      setBcmExceptionForm({ email: "", first_name: "", last_name: "", role: "executive_sponsor" });
      fetchData();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to send special invitation"));
    } finally {
      setBcmExceptionInviting(false);
    }
  };

  const getInviteStatus = (invite) => {
    if (invite.status === "accepted") {
      return { label: "Accepted", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 };
    }
    const expires = new Date(invite.expires_at);
    if (new Date() > expires) {
      return { label: "Expired", color: "bg-red-100 text-red-700", icon: XCircle };
    }
    if (invite.display_status === "email_failed" || invite.delivery === "email_failed") {
      return { label: "Email failed", color: "bg-orange-100 text-orange-700", icon: AlertTriangle };
    }
    return { label: "Pending", color: "bg-amber-100 text-amber-700", icon: Clock };
  };

  // Department management functions
  const openDeptDialog = (dept = null) => {
    const _u = (id) => users.find(u => u.id === id) || {};
    setDeptError("");
    if (dept) {
      const po = _u(dept.primary_owner_id || dept.team_lead_id);
      const so = _u(dept.secondary_owner_id);
      setEditingDept(dept);
      setDeptForm({
        name: dept.name || "",
        description: dept.description || "",
        criticality: dept.criticality || "medium",
        location_ids: dept.location_ids || [],
        work_from_home: !!dept.work_from_home,
        primary_owner_id: dept.primary_owner_id || dept.team_lead_id || "",
        secondary_owner_id: dept.secondary_owner_id || "",
        primary_owner_mode: "existing",
        secondary_owner_mode: "existing",
        primary_owner_dirty: false,
        secondary_owner_dirty: false,
        primary_owner_pending: (!(dept.primary_owner_id || dept.team_lead_id) && (dept.primary_owner_name || dept.team_lead_name)) ? (dept.primary_owner_name || dept.team_lead_name) : "",
        secondary_owner_pending: (!dept.secondary_owner_id && dept.secondary_owner_name) ? dept.secondary_owner_name : "",
        primary_owner_pending_email: (!(dept.primary_owner_id || dept.team_lead_id) && (dept.primary_owner_email || dept.team_lead_email)) ? (dept.primary_owner_email || dept.team_lead_email) : "",
        secondary_owner_pending_email: (!dept.secondary_owner_id && dept.secondary_owner_email) ? dept.secondary_owner_email : "",
        primary_new: { first_name: "", last_name: "", email: "" },
        secondary_new: { first_name: "", last_name: "", email: "" },
        primary_contact: dept.primary_contact || "",
        secondary_contact: dept.secondary_contact || "",
        primary_owner_phone: po.phone || "",
        primary_owner_work_location: po.work_location || "",
        secondary_owner_phone: so.phone || "",
        secondary_owner_work_location: so.work_location || "",
        primary_owner_home: { home_street: "", home_city: "", home_state: "", home_postal_code: "", home_country: "" },
        secondary_owner_home: { home_street: "", home_city: "", home_state: "", home_postal_code: "", home_country: "" }
      });
    } else {
      setEditingDept(null);
      setDeptForm({ 
        name: "", 
        description: "", 
        criticality: "medium", 
        location_ids: [],
        work_from_home: false,
        primary_owner_id: "",
        secondary_owner_id: "",
        primary_owner_mode: "existing",
        secondary_owner_mode: "existing",
        primary_owner_dirty: false,
        secondary_owner_dirty: false,
        primary_owner_pending: "",
        secondary_owner_pending: "",
        primary_owner_pending_email: "",
        secondary_owner_pending_email: "",
        primary_new: { first_name: "", last_name: "", email: "" },
        secondary_new: { first_name: "", last_name: "", email: "" },
        primary_contact: "",
        secondary_contact: "",
        primary_owner_phone: "",
        primary_owner_work_location: "",
        secondary_owner_phone: "",
        secondary_owner_work_location: "",
        primary_owner_home: { home_street: "", home_city: "", home_state: "", home_postal_code: "", home_country: "" },
        secondary_owner_home: { home_street: "", home_city: "", home_state: "", home_postal_code: "", home_country: "" }
      });
    }
    setShowDeptDialog(true);
  };

  const fetchAiDepartmentSuggestions = async () => {
    setAiSuggestLoading(true);
    try {
      const res = await api.post("/ai-assistant/suggest-departments");
      if (res.data?.success) {
        setAiSuggestedDepts(res.data.suggested_departments || []);
      }
    } catch (e) {
      console.error("AI department suggestions failed:", e);
      toast.error("Could not load AI suggestions");
    } finally {
      setAiSuggestLoading(false);
    }
  };

  const applyAiSuggestion = (dept) => {
    setDeptForm(prev => ({
      ...prev,
      name: dept.name,
      description: dept.description || "",
      criticality: dept.criticality === "critical" ? "high" : dept.criticality || "medium",
    }));
    setAiSuggestedDepts([]);
  };

  const _resolveOwner = (pfx) => {
    if (deptForm[`${pfx}_owner_mode`] === "new") {
      const n = deptForm[`${pfx}_new`] || {};
      const full = `${n.first_name || ""} ${n.last_name || ""}`.trim();
      return { id: null, name: full || null, email: (n.email || "").trim() || null };
    }
    const u = users.find(x => x.id === deptForm[`${pfx}_owner_id`]);
    return {
      id: deptForm[`${pfx}_owner_id`] || null,
      name: u ? `${u.first_name} ${u.last_name}` : null,
      email: u?.email || null,
    };
  };

  const saveDepartment = async () => {
    setDeptError("");
    try {
      const p = _resolveOwner("primary");
      const s = _resolveOwner("secondary");
      const { primary_owner_home, secondary_owner_home, primary_new, secondary_new,
        primary_owner_mode, secondary_owner_mode,
        primary_owner_dirty, secondary_owner_dirty,
        primary_owner_pending, secondary_owner_pending, ...deptRest } = deptForm;
      const payload = {
        ...deptRest,
        primary_owner_id: p.id,
        secondary_owner_id: s.id,
        team_lead_id: p.id, // Backwards compatibility
        team_lead_email: p.email,
        team_lead_name: p.name,
        primary_owner_name: p.name,
        primary_owner_email: p.email,
        secondary_owner_name: s.name,
        secondary_owner_email: s.email,
      };

      // Item 1b — on Edit, send a slot's fields ONLY if the user changed that
      // slot; an untouched (incl. pending) slot is omitted so PUT leaves it be.
      if (editingDept) {
        if (!primary_owner_dirty) {
          delete payload.primary_owner_id; delete payload.primary_owner_name; delete payload.primary_owner_email;
          delete payload.primary_contact; delete payload.team_lead_id; delete payload.team_lead_name; delete payload.team_lead_email;
        }
        if (!secondary_owner_dirty) {
          delete payload.secondary_owner_id; delete payload.secondary_owner_name; delete payload.secondary_owner_email;
          delete payload.secondary_contact;
        }
      }

      let deptId = editingDept?.id;
      if (editingDept) {
        await api.put(`/bcm-admin/departments/${editingDept.id}`, payload);
        toast.success("Department updated successfully");
      } else {
        const res = await api.post("/bcm-admin/departments", payload);
        deptId = res.data?.id;
        toast.success("Department created successfully");
        completeChecklistStep("first_department");
        fetchTrialStatus();
      }
      // B5 — home address (Work From Home only) via the private-contact endpoint.
      const isWFH = (v) => (v || "").toLowerCase() === "work_from_home";
      if (deptForm.primary_owner_id && isWFH(deptForm.primary_owner_work_location) && primary_owner_home.home_country) {
        await api.put(`/bcm-admin/users/${deptForm.primary_owner_id}/private-contact`, primary_owner_home).catch(() => {});
      }
      if (deptForm.secondary_owner_id && isWFH(deptForm.secondary_owner_work_location) && secondary_owner_home.home_country) {
        await api.put(`/bcm-admin/users/${deptForm.secondary_owner_id}/private-contact`, secondary_owner_home).catch(() => {});
      }
      setShowDeptDialog(false);
      fetchData();
      return deptId;
    } catch (error) {
      if (error.response?.status === 403 && error.response?.data?.detail?.includes("Trial limit")) {
        triggerUpgradeWall("Department", departments.length, 3);
      } else {
        const msg = extractErrorMessage(error, "Failed to save department");
        setDeptError(msg);
        toast.error(msg);
      }
      return null;
    }
  };

  // B3 — save then send Department Head invitation(s) for whichever slots have
  // an owner. Reuses the same invite-owner route/template/expiry/audit.
  const saveAndInviteHeads = async () => {
    setDeptError("");
    setInvitingHeads(true);
    try {
      const deptId = await saveDepartment();
      if (!deptId) return;
      const slots = [];
      if (deptForm.primary_owner_mode === "new" ? deptForm.primary_new.email : (deptForm.primary_owner_id || deptForm.primary_owner_pending)) slots.push("primary");
      if (deptForm.secondary_owner_mode === "new" ? deptForm.secondary_new.email : (deptForm.secondary_owner_id || deptForm.secondary_owner_pending)) slots.push("secondary");
      if (slots.length === 0) {
        toast.error("Assign a Primary or Backup Department Head first");
        return;
      }
      // B3 — for a slot that already has a pending invitation, RESEND it using
      // the same endpoint the Invitations table uses (find the id via the
      // existing GET /auth/invitations, matched by the pending email); slots
      // without a pending invite go through invite-owner as before.
      let pendingInvites = [];
      try { pendingInvites = (await api.get("/auth/invitations")).data || []; } catch (e) { pendingInvites = []; }
      const findPending = (email) => pendingInvites.find(
        (i) => i.status === "pending" && (i.email || "").toLowerCase() === (email || "").toLowerCase());
      let sent = 0;
      for (const ot of slots) {
        const pendEmail = ot === "primary" ? deptForm.primary_owner_pending_email : deptForm.secondary_owner_pending_email;
        const pend = pendEmail ? findPending(pendEmail) : null;
        try {
          if (pend) {
            await api.post(`/auth/invitations/${pend.id}/resend`);
            toast.success(`Invitation resent to ${pend.email}`);
          } else {
            const res = await api.post(`/bcm-admin/departments/${deptId}/invite-owner`, { owner_type: ot });
            const d = res.data || {};
            if (d.delivery && d.delivery !== "sent") {
              toast.warning(d.message || `Invitation saved, but the email could not be sent.`);
            } else {
              toast.success(d.message || `${ot} Department Head invited`);
            }
          }
          sent += 1;
        } catch (e) {
          const m = extractErrorMessage(e, `Failed to invite ${ot} Department Head`);
          setDeptError(m);
          toast.error(m);
        }
      }
      if (sent > 0) fetchData();
    } finally {
      setInvitingHeads(false);
    }
  };

  const deleteDepartment = async (deptId) => {
    if (!confirm("Are you sure you want to delete this department?")) return;
    try {
      await api.delete(`/bcm-admin/departments/${deptId}`);
      toast.success("Department deleted");
      fetchData();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to delete department"));
    }
  };

  // Organization Foundation functions
  const saveOrgFoundation = async () => {
    try {
      await api.put("/bcm-admin/organization/foundation", orgFoundation);
      toast.success("Mission, vision and values updated");
      setEditingFoundation(false);
      fetchData();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to save foundation"));
    }
  };

  // Apply AI mission_vision suggestions into the foundation edit form.
  const handleAcceptFoundationSuggestions = (items) => {
    let mission = "", vision = "";
    const values = [];
    (items || []).forEach((it) => {
      const s = typeof it === "string" ? it : (it.mission || it.vision || it.label || "");
      const low = String(s).toLowerCase();
      if (low.startsWith("mission:")) mission = String(s).split(":").slice(1).join(":").trim();
      else if (low.startsWith("vision:")) vision = String(s).split(":").slice(1).join(":").trim();
      else if (s) values.push(String(s));
    });
    setOrgFoundation((prev) => ({
      mission_statement: mission || prev.mission_statement,
      vision: vision || prev.vision,
      core_values: values.length ? Array.from(new Set([...(prev.core_values || []), ...values])) : prev.core_values,
    }));
    setEditingFoundation(true);
    toast.success("AI suggestions applied — review and click Save");
  };

  const addCoreValue = () => {
    if (newCoreValue.trim() && !orgFoundation.core_values.includes(newCoreValue.trim())) {
      setOrgFoundation(prev => ({
        ...prev,
        core_values: [...prev.core_values, newCoreValue.trim()]
      }));
      setNewCoreValue("");
    }
  };

  const removeCoreValue = (value) => {
    setOrgFoundation(prev => ({
      ...prev,
      core_values: prev.core_values.filter(v => v !== value)
    }));
  };

  // Executive management functions
  const openExecDialog = (exec = null) => {
    if (exec) {
      setEditingExec(exec);
      setExecForm({
        name: exec.name || "",
        designation: exec.designation || "",
        role_type: exec.role_type || "executive",
        email: exec.email || "",
        phone: exec.phone || "",
        notes: exec.notes || ""
      });
    } else {
      setEditingExec(null);
      setExecForm({
        name: "",
        designation: "",
        role_type: "executive",
        email: "",
        phone: "",
        notes: ""
      });
    }
    setShowExecDialog(true);
  };

  const saveExecutive = async () => {
    try {
      if (editingExec) {
        await api.put(`/bcm-admin/executives/${editingExec.id}`, execForm);
        toast.success("Executive updated");
      } else {
        await api.post("/bcm-admin/executives", execForm);
        toast.success("Executive added");
      }
      setShowExecDialog(false);
      fetchData();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to save executive"));
    }
  };

  const deleteExecutive = async (execId) => {
    if (!confirm("Are you sure you want to remove this executive?")) return;
    try {
      await api.delete(`/bcm-admin/executives/${execId}`);
      toast.success("Executive removed");
      fetchData();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to remove executive"));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-sky-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="org-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Organization Management</h1>
          <p className="text-slate-600 mt-1">Manage your team and monitor BCM compliance</p>
        </div>
        <div className="flex gap-2">
          {(user?.role === "bcm_manager" || user?.role === "admin") && (
            <Button
              onClick={() => setShowBcmExceptionDialog(true)}
              variant="outline"
              className="relative border-purple-300 text-purple-700 hover:bg-purple-50"
              data-testid="bcm-special-invite-btn"
              title="BCM-only: invite Executive Sponsor, External Auditor, or Deputy BCM Manager"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Special Invite
              <span className="ml-2 text-[9px] font-bold bg-purple-600 text-white px-1.5 py-0.5 rounded-full uppercase tracking-wide">
                BCM
              </span>
            </Button>
          )}
          <Button 
            onClick={() => setShowITDRPInviteDialog(true)}
            variant="outline"
            className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
            data-testid="invite-itdrp-btn"
          >
            <Shield className="w-4 h-4 mr-2" />
            Invite IT-DRP Manager
          </Button>
          <Button 
            onClick={() => setShowInviteDialog(true)} 
            className="bg-sky-700 hover:bg-sky-800"
            data-testid="invite-team-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Invite Team Member
          </Button>
        </div>
      </div>

      {/* Organization Info Card */}
      {org && (
        <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-sky-100">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-xl bg-sky-700 flex items-center justify-center">
                  <Building2 className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">{org.name}</h2>
                  <p className="text-sm text-slate-600">{org.address}</p>
                  <p className="text-sm text-slate-500">{org.phone}</p>
                  {org.compliance_identifiers?.ein && (
                    <p className="text-sm text-slate-600" data-testid="org-ein">
                      EIN: {org.compliance_identifiers.ein}
                      {(org.enrichment_field_sources?.ein?.source === "manual" || org.compliance_identifiers?.ein_source === "manual") && (
                        <span className="ml-2 text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5">Entered by you</span>
                      )}
                    </p>
                  )}
                  {(org.certifications || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2" data-testid="org-certifications">
                      {org.certifications.map((c, i) => {
                        const exp = c.expiry && new Date(c.expiry) < new Date();
                        return (
                          <span key={i} className="text-[11px] bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5 flex items-center gap-1" data-testid={`org-cert-${i}`}>
                            {c.type === "Other" ? c.name : c.type}
                            {exp && <span className="text-[9px] text-red-700 bg-red-50 border border-red-200 rounded-full px-1" data-testid={`org-cert-expired-${i}`}>Expired</span>}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col items-start md:items-end gap-2">
                <p className="text-sm font-medium text-sky-700">Organization Code</p>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold tracking-widest text-slate-900" data-testid="org-code">
                    {org.code}
                  </span>
                  <Button variant="outline" size="sm" onClick={copyOrgCode} data-testid="copy-code-btn">
                    {codeCopied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-slate-500">Share this code with new team members</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Exercise Reminders Alert */}
      {reminders?.summary && (reminders.summary.overdue > 0 || reminders.summary.urgent > 0) && (
        <Card className={reminders.summary.overdue > 0 ? "border-red-200 bg-red-50" : "border-amber-200 bg-amber-50"}>
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Bell className={`w-5 h-5 mt-0.5 ${reminders.summary.overdue > 0 ? "text-red-600" : "text-amber-600"}`} />
              <div className="flex-1">
                <h3 className={`font-semibold ${reminders.summary.overdue > 0 ? "text-red-800" : "text-amber-800"}`}>
                  Exercise Reminders
                </h3>
                <div className="space-y-1 mt-2">
                  {reminders.reminders?.slice(0, 3).map((reminder, idx) => (
                    <div key={idx} className={`text-sm ${reminder.urgency === 'overdue' ? 'text-red-700 font-medium' : 'text-amber-700'}`}>
                      • {reminder.message}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      {stats && stats.users && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <Users className="w-6 h-6 mx-auto text-sky-700 mb-2" />
              <p className="text-2xl font-bold text-slate-900">{stats.users?.total || 0}</p>
              <p className="text-xs text-slate-500">Total Users</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Shield className="w-6 h-6 mx-auto text-purple-600 mb-2" />
              <p className="text-2xl font-bold text-slate-900">{stats.users?.bcm_managers || 0}</p>
              <p className="text-xs text-slate-500">BCM Managers</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <FileText className="w-6 h-6 mx-auto text-blue-600 mb-2" />
              <p className="text-2xl font-bold text-slate-900">{stats.policies?.approved || 0}</p>
              <p className="text-xs text-slate-500">Approved Policies</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <ClipboardCheck className="w-6 h-6 mx-auto text-emerald-600 mb-2" />
              <p className="text-2xl font-bold text-slate-900">{stats.exercises?.completed || 0}</p>
              <p className="text-xs text-slate-500">Exercises Done</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <BookOpen className="w-6 h-6 mx-auto text-amber-600 mb-2" />
              <p className="text-2xl font-bold text-slate-900">{stats.training?.compliance_rate || 0}%</p>
              <p className="text-xs text-slate-500">Training Rate</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <AlertTriangle className="w-6 h-6 mx-auto text-red-600 mb-2" />
              <p className="text-2xl font-bold text-slate-900">{stats.analysis?.high_risks || 0}</p>
              <p className="text-xs text-slate-500">High Risks</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Organization Foundation - Mission, Vision, Core Values */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-emerald-600" />
                Mission, Vision & Values
              </CardTitle>
              <CardDescription>Mission Statement, Vision, and Core Values</CardDescription>
            </div>
            <Button 
              variant={editingFoundation ? "default" : "outline"} 
              size="sm" 
              onClick={() => editingFoundation ? saveOrgFoundation() : setEditingFoundation(true)}
              data-testid="edit-foundation-btn"
            >
              {editingFoundation ? <><Save className="w-4 h-4 mr-1" /> Save</> : <><Edit2 className="w-4 h-4 mr-1" /> Edit</>}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <AISuggestionsBanner
            module="mission_vision"
            onAccept={handleAcceptFoundationSuggestions}
          />
          <div className="grid md:grid-cols-2 gap-6">
            {/* Mission Statement */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-sm font-medium">
                <Target className="w-4 h-4 text-emerald-600" />
                Organization Mission Statement *
              </Label>
              {editingFoundation ? (
                <Textarea
                  value={orgFoundation.mission_statement}
                  onChange={e => setOrgFoundation(prev => ({ ...prev, mission_statement: e.target.value }))}
                  placeholder="Define your organization's purpose and primary objectives..."
                  className="min-h-[100px]"
                  data-testid="mission-input"
                />
              ) : (
                <div className="p-3 bg-slate-50 rounded-lg border min-h-[100px]">
                  <p className="text-slate-700">{orgFoundation.mission_statement || <span className="text-slate-400 italic">Not defined</span>}</p>
                </div>
              )}
            </div>
            
            {/* Vision */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-sm font-medium">
                <Eye className="w-4 h-4 text-blue-600" />
                Organization Vision
              </Label>
              {editingFoundation ? (
                <Textarea
                  value={orgFoundation.vision}
                  onChange={e => setOrgFoundation(prev => ({ ...prev, vision: e.target.value }))}
                  placeholder="Describe your organization's long-term aspirations..."
                  className="min-h-[100px]"
                  data-testid="vision-input"
                />
              ) : (
                <div className="p-3 bg-slate-50 rounded-lg border min-h-[100px]">
                  <p className="text-slate-700">{orgFoundation.vision || <span className="text-slate-400 italic">Not defined</span>}</p>
                </div>
              )}
            </div>
          </div>
          
          {/* Core Values */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-sm font-medium">
              <Heart className="w-4 h-4 text-red-500" />
              Core Values
            </Label>
            {editingFoundation && (
              <>
                {/* Predefined Core Values Selection */}
                <div className="flex flex-wrap gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200 mb-2">
                  <span className="text-xs text-blue-600 font-medium w-full mb-1">Click to add common values:</span>
                  {['Integrity', 'Excellence', 'Innovation', 'Accountability', 'Teamwork', 'Customer Focus', 'Respect', 'Safety', 'Sustainability', 'Transparency'].map(value => (
                    <Badge 
                      key={value} 
                      variant="outline" 
                      className={`cursor-pointer transition-all ${
                        orgFoundation.core_values.includes(value) 
                          ? 'bg-blue-100 text-blue-700 border-blue-300' 
                          : 'bg-white hover:bg-blue-50 text-slate-600 border-slate-300'
                      }`}
                      onClick={() => {
                        if (!orgFoundation.core_values.includes(value)) {
                          setOrgFoundation(prev => ({ ...prev, core_values: [...prev.core_values, value] }));
                        }
                      }}
                    >
                      {orgFoundation.core_values.includes(value) ? '✓ ' : '+ '}{value}
                    </Badge>
                  ))}
                </div>
                {/* Custom Core Value Input */}
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newCoreValue}
                    onChange={e => setNewCoreValue(e.target.value)}
                    onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addCoreValue())}
                    placeholder="Add a custom core value..."
                    data-testid="core-value-input"
                  />
                  <Button type="button" onClick={addCoreValue} variant="outline" size="sm">Add</Button>
                </div>
              </>
            )}
            <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-lg border min-h-[50px]">
              {orgFoundation.core_values.length > 0 ? (
                orgFoundation.core_values.map((value, idx) => (
                  <Badge key={idx} variant="secondary" className="bg-red-50 text-red-700 border border-red-200">
                    {value}
                    {editingFoundation && (
                      <button onClick={() => removeCoreValue(value)} className="ml-1 hover:text-red-900">×</button>
                    )}
                  </Badge>
                ))
              ) : (
                <span className="text-slate-400 italic text-sm">No core values defined</span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sites (S1) */}
      <SitesCard locations={locations} api={api} onChanged={fetchData}
        canManage={user?.role === "bcm_manager" || user?.role === "admin"} />

      {/* Departments & Team Leads Section */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Building className="w-5 h-5 text-sky-700" />
                Department Details
              </CardTitle>
              <CardDescription>Assign Resiliency Role to Department Head</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setShowBulkInviteModal(true)}
                size="sm"
                variant="outline"
                className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                data-testid="dept-bulk-invite-btn"
              >
                <UserPlus className="w-4 h-4 mr-1" />
                Bulk CSV Invite
              </Button>
              <Button onClick={() => openDeptDialog()} size="sm" data-testid="add-dept-btn">
                <Plus className="w-4 h-4 mr-1" />
                Add Department
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {departments.length === 0 ? (
            <p className="text-slate-500 text-center py-6">No departments configured. Add departments to assign Department Heads.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Department</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Location(s)</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Criticality</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Primary Department Head</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Backup Department Head</th>
                    <th className="text-right py-3 px-4 font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {departments.map((dept) => {
                    const deptLocations = (dept.location_ids || []).map(id => 
                      locations.find(l => l.id === id)
                    ).filter(Boolean);
                    // B5 — flag a head who also holds another role (or heads another dept).
                    const _headConflict = (() => {
                      const oids = [dept.primary_owner_id, dept.secondary_owner_id].filter(Boolean);
                      for (const oid of oids) {
                        const ou = users.find(u => u.id === oid);
                        if (ou && ou.role && ou.role !== "department_head" &&
                            ["bcm_manager", "admin", "process_manager", "it_drp_manager", "app_manager", "it_function_manager"].includes(ou.role)) {
                          return true;
                        }
                        const other = departments.find(d => d.id !== dept.id &&
                          (d.primary_owner_id === oid || d.secondary_owner_id === oid));
                        if (other) return true;
                      }
                      return false;
                    })();
                    
                    return (
                      <tr key={dept.id} className="border-b hover:bg-slate-50" data-testid={`dept-row-${dept.id}`}>
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium text-slate-900">{dept.name}</p>
                            {dept.description && (
                              <p className="text-sm text-slate-500">{dept.description}</p>
                            )}
                            {_headConflict && (
                              <p className="text-xs text-amber-700 flex items-center gap-1 mt-1" data-testid={`dept-head-conflict-${dept.id}`}>
                                <AlertTriangle className="w-3 h-3" /> Department Head also holds another role — reassign.
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {(deptLocations.length > 0 || dept.work_from_home) ? (
                            <div className="flex flex-wrap gap-1">
                              {deptLocations.map((loc, idx) => (
                                <Badge key={idx} variant="outline" className="bg-blue-50 text-blue-700 text-xs">
                                  <MapPin className="w-3 h-3 mr-1" />
                                  {loc.name}{loc.status === "retired" ? " (retired)" : ""}
                                </Badge>
                              ))}
                              {dept.work_from_home && (
                                <Badge variant="outline" className="bg-amber-50 text-amber-700 text-xs" data-testid={`dept-wfh-badge-${dept.id}`}>
                                  <Home className="w-3 h-3 mr-1" />
                                  Work From Home
                                </Badge>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-400 text-sm">—</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={
                            dept.criticality === 'high' ? 'bg-red-100 text-red-700' :
                            dept.criticality === 'medium' ? 'bg-amber-100 text-amber-700' :
                            'bg-slate-100 text-slate-700'
                          }>
                            {dept.criticality}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          {(dept.primary_owner_name || dept.team_lead_name) ? (
                            <div className="flex items-center gap-2">
                              <UserCheck className="w-4 h-4 text-emerald-600" />
                              <div className="flex-1">
                                <p className="text-sm font-medium text-slate-900">{dept.primary_owner_name || dept.team_lead_name}</p>
                                <p className="text-xs text-slate-500">{dept.primary_owner_email || dept.team_lead_email}</p>
                                {dept.primary_contact && (
                                  <p className="text-xs text-slate-400 flex items-center gap-1">
                                    <Phone className="w-3 h-3" /> {dept.primary_contact}
                                  </p>
                                )}
                                {(() => { const ou = users.find(u => u.id === (dept.primary_owner_id || dept.team_lead_id)); return ou && (ou.phone || ou.work_location) ? (
                                  <p className="text-xs text-slate-500 flex items-center gap-1" data-testid={`primary-owner-contact-${dept.id}`}>
                                    {ou.phone && <><Phone className="w-3 h-3" /> {ou.phone}</>}
                                    {ou.work_location && <span className="ml-1"><MapPin className="w-3 h-3 inline" /> {ou.work_location.toLowerCase() === "work_from_home" ? "Work From Home" : ou.work_location}</span>}
                                  </p>
                                ) : null; })()}
                              </div>
                              {dept.primary_invite_status === "accepted" ? (
                                <Badge className="bg-emerald-100 text-emerald-700 text-xs" data-testid={`primary-invited-${dept.id}`}>
                                  <CheckCircle2 className="w-3 h-3 mr-1" /> Dept Head
                                </Badge>
                              ) : dept.primary_invite_status === "pending" ? (
                                <Badge className="bg-amber-100 text-amber-700 text-xs" data-testid={`primary-pending-${dept.id}`}>
                                  <Clock className="w-3 h-3 mr-1" /> Pending
                                </Badge>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-sky-700 border-sky-300 hover:bg-sky-50 h-7 text-xs"
                                  onClick={() => handleInviteDeptOwner(dept.id, "primary")}
                                  disabled={invitingOwner === `${dept.id}-primary`}
                                  data-testid={`invite-primary-${dept.id}`}
                                >
                                  {invitingOwner === `${dept.id}-primary` ? (
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                  ) : (
                                    <Send className="w-3 h-3 mr-1" />
                                  )}
                                  Invite
                                </Button>
                              )}
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-slate-500 hover:text-sky-700 hover:bg-sky-50 h-7 text-xs gap-1"
                              onClick={() => openDeptDialog(dept)}
                              data-testid={`assign-primary-${dept.id}`}
                            >
                              <UserPlus className="w-3.5 h-3.5" />
                              Assign Department Head
                            </Button>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {dept.secondary_owner_name ? (
                            <div className="flex items-center gap-2">
                              <UserCheck className="w-4 h-4 text-blue-600" />
                              <div className="flex-1">
                                <p className="text-sm font-medium text-slate-900">{dept.secondary_owner_name}</p>
                                <p className="text-xs text-slate-500">{dept.secondary_owner_email}</p>
                                {dept.secondary_contact && (
                                  <p className="text-xs text-slate-400 flex items-center gap-1">
                                    <Phone className="w-3 h-3" /> {dept.secondary_contact}
                                  </p>
                                )}
                              </div>
                              {dept.secondary_invite_status === "accepted" ? (
                                <Badge className="bg-emerald-100 text-emerald-700 text-xs" data-testid={`secondary-invited-${dept.id}`}>
                                  <CheckCircle2 className="w-3 h-3 mr-1" /> Dept Head
                                </Badge>
                              ) : dept.secondary_invite_status === "pending" ? (
                                <Badge className="bg-amber-100 text-amber-700 text-xs" data-testid={`secondary-pending-${dept.id}`}>
                                  <Clock className="w-3 h-3 mr-1" /> Pending
                                </Badge>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-sky-700 border-sky-300 hover:bg-sky-50 h-7 text-xs"
                                  onClick={() => handleInviteDeptOwner(dept.id, "secondary")}
                                  disabled={invitingOwner === `${dept.id}-secondary`}
                                  data-testid={`invite-secondary-${dept.id}`}
                                >
                                  {invitingOwner === `${dept.id}-secondary` ? (
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                  ) : (
                                    <Send className="w-3 h-3 mr-1" />
                                  )}
                                  Invite
                                </Button>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-400 text-sm">—</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-emerald-700 border-emerald-300 hover:bg-emerald-50 h-7 text-xs gap-1"
                              onClick={() => setInlineInvite({ open: true, dept })}
                              data-testid={`inline-invite-dh-${dept.id}`}
                              title="Invite Department Head with department pre-selected"
                            >
                              <UserPlus className="w-3.5 h-3.5" />
                              Invite DH
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openDeptDialog(dept)}
                              data-testid={`edit-dept-${dept.id}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => deleteDepartment(dept.id)}
                              data-testid={`delete-dept-${dept.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Executives Section */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-amber-600" />
                Key Executives & Decision Makers
              </CardTitle>
              <CardDescription>Key executives, decision makers, and sponsors for BCM program</CardDescription>
            </div>
            <Button onClick={() => openExecDialog()} size="sm" data-testid="add-exec-btn">
              <Plus className="w-4 h-4 mr-1" />
              Add Executive
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {executives.length === 0 ? (
            <p className="text-slate-500 text-center py-6">No executives configured. Add key decision makers and sponsors.</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {executives.map((exec) => (
                <div key={exec.id} className="p-4 border rounded-lg hover:shadow-md transition-shadow" data-testid={`exec-card-${exec.id}`}>
                  <div className="flex justify-between items-start">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-full ${
                        exec.role_type === 'executive' ? 'bg-amber-100' :
                        exec.role_type === 'decision_maker' ? 'bg-blue-100' :
                        'bg-purple-100'
                      }`}>
                        <Crown className={`w-4 h-4 ${
                          exec.role_type === 'executive' ? 'text-amber-600' :
                          exec.role_type === 'decision_maker' ? 'text-blue-600' :
                          'text-purple-600'
                        }`} />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{exec.name}</p>
                        <p className="text-sm text-slate-600">{exec.designation}</p>
                        {exec.location_id && (() => { const l = locations.find(x => x.id === exec.location_id); return l ? (
                          <p className="text-xs text-slate-500 flex items-center gap-1" data-testid={`exec-location-${exec.id}`}><MapPin className="w-3 h-3" /> {l.name}</p>
                        ) : null; })()}
                        <Badge variant="outline" className="mt-1 text-xs">
                          {exec.role_type === 'executive' ? 'Executive' :
                           exec.role_type === 'decision_maker' ? 'Decision Maker' :
                           'Sponsor'}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openExecDialog(exec)}>
                        <Edit2 className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteExecutive(exec.id)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  {(exec.email || exec.phone) && (
                    <div className="mt-3 pt-3 border-t space-y-1">
                      {exec.email && (
                        <p className="text-xs text-slate-500 flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {exec.email}
                        </p>
                      )}
                      {exec.phone && (
                        <p className="text-xs text-slate-500 flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {exec.phone}
                        </p>
                      )}
                    </div>
                  )}
                  {exec.notes && (
                    <p className="mt-2 text-xs text-slate-400 italic">{exec.notes}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upcoming Exercises */}
      {stats?.exercises?.upcoming?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              Upcoming Exercises
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats.exercises.upcoming.map((ex, idx) => (
                <div key={idx} className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-900">{ex.title}</p>
                    <p className="text-sm text-slate-500 capitalize">{ex.exercise_type.replace('_', ' ')}</p>
                  </div>
                  <span className="text-sm text-blue-700">{new Date(ex.scheduled_date).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-sky-700" />
            Team Members
          </CardTitle>
          <CardDescription>Manage users in your organization</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-medium text-slate-600">User</th>
                  <th className="text-left py-3 px-4 font-medium text-slate-600">Role</th>
                  <th className="text-left py-3 px-4 font-medium text-slate-600">Department</th>
                  <th className="text-left py-3 px-4 font-medium text-slate-600">Training</th>
                  <th className="text-left py-3 px-4 font-medium text-slate-600">Joined</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-medium text-slate-900">{u.first_name} {u.last_name}</p>
                        <p className="text-sm text-slate-500">{u.email}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        u.role === 'bcm_manager' ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-700'
                      }`}>
                        {resolveRoleLabel(u)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-slate-600">
                      {canSetUserDept(u) ? (
                        <div className="flex items-center gap-2" data-testid={`dept-editor-${u.id}`}>
                          <Select
                            value={deptEdits[u.id] || ""}
                            onValueChange={(v) => setDeptEdits((prev) => ({ ...prev, [u.id]: v }))}
                          >
                            <SelectTrigger className="h-8 w-40" data-testid={`dept-select-${u.id}`}>
                              <SelectValue placeholder={u.department || "Department"} />
                            </SelectTrigger>
                            <SelectContent>
                              {deptOptionsFor(u).map((d) => (
                                <SelectItem key={d.id} value={d.id} data-testid={`dept-option-${u.id}-${d.id}`}>
                                  {d.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8"
                            disabled={!deptEdits[u.id] || savingDeptFor === u.id}
                            onClick={() => saveUserDepartment(u)}
                            data-testid={`save-dept-${u.id}`}
                          >
                            Save
                          </Button>
                        </div>
                      ) : (
                        u.department || '—'
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm text-slate-600">{u.training_completed || 0} completed</span>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-500">
                      {new Date(u.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openRoleDialog(u)}
                          disabled={u.id === user?.id}
                          data-testid={`edit-role-${u.id}`}
                        >
                          <UserCog className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => confirmDeleteUser(u)}
                          disabled={u.id === user?.id}
                          data-testid={`delete-user-${u.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Pending Invitations */}
      {invitations.length > 0 && (
        <Card data-testid="pending-invitations-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-sky-700" />
              Invitations
            </CardTitle>
            <CardDescription>Team members invited to join your organization — includes email delivery status. Use Resend or Revoke as needed.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Invitee</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Role</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Invited By</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Sent</th>
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Expires</th>
                    <th className="text-right py-3 px-4 font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invitations.map((inv) => {
                    const status = getInviteStatus(inv);
                    const StatusIcon = status.icon;
                    return (
                      <tr key={inv.id} className="border-b hover:bg-slate-50" data-testid={`invite-row-${inv.id}`}>
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium text-slate-900">{inv.first_name} {inv.last_name}</p>
                            <p className="text-sm text-slate-500">{inv.email}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600">{resolveRoleLabel(inv)}</td>
                        <td className="py-3 px-4">
                          <Badge className={`${status.color} flex items-center gap-1 w-fit`}>
                            <StatusIcon className="w-3 h-3" />
                            {status.label}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-slate-600">{inv.invited_by}</td>
                        <td className="py-3 px-4 text-sm text-slate-500">
                          {inv.invited_at ? new Date(inv.invited_at).toLocaleDateString() : "—"}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-500">
                          {new Date(inv.expires_at).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4">
                          {inv.status === "pending" && (
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleResendInvite(inv.id)}
                                title="Resend invitation"
                                data-testid={`resend-invite-${inv.id}`}
                              >
                                <RefreshCw className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleCancelInvite(inv.id)}
                                title="Cancel invitation"
                                data-testid={`cancel-invite-${inv.id}`}
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invite Team Member — role-aware modal (fetches allowed roles + reports real email delivery status) */}
      <RoleBasedInviteModal
        open={showInviteDialog}
        onClose={() => setShowInviteDialog(false)}
        onInvited={() => { setShowInviteDialog(false); fetchData?.(); }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove User</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove {userToDelete?.first_name} {userToDelete?.last_name} ({userToDelete?.email}) from your organization?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteUser}>Remove User</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change User Role</DialogTitle>
            <DialogDescription>
              Update the role for {userToEdit?.first_name} {userToEdit?.last_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Select value={selectedRole} onValueChange={setSelectedRole}>
              <SelectTrigger>
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bcm_manager">{resolveRoleLabel({ role: 'bcm_manager' })} - Full access to organization BCM</SelectItem>
                <SelectItem value="process_manager">{resolveRoleLabel({ role: 'process_manager' })} - Limited access</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowRoleDialog(false)}>Cancel</Button>
              <Button onClick={handleUpdateRole}>Update Role</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Department Dialog */}
      <Dialog open={showDeptDialog} onOpenChange={setShowDeptDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingDept ? 'Edit Department' : 'Add Department'}</DialogTitle>
            <DialogDescription>
              {editingDept ? 'Update department details and assign Department Heads' : 'Create a new department and assign Department Heads'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4 max-h-[70vh] overflow-y-auto pr-2">
            {/* AI Suggestions Panel (only for new departments) */}
            {!editingDept && (
              <div className="border border-sky-200 bg-sky-50/50 rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-sky-700" />
                    <span className="text-sm font-medium text-sky-900">AI Department Suggestions</span>
                    <span className="text-xs text-sky-600">Based on your industry</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchAiDepartmentSuggestions}
                    disabled={aiSuggestLoading}
                    className="text-sky-700 border-sky-200 hover:bg-sky-100 h-7 text-xs"
                    data-testid="ai-suggest-dept-btn"
                  >
                    {aiSuggestLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />}
                    {aiSuggestedDepts.length > 0 ? "Refresh" : "Get Suggestions"}
                  </Button>
                </div>
                {aiSuggestedDepts.length > 0 && (
                  <div className="flex flex-wrap gap-1.5" data-testid="ai-dept-suggestions-list">
                    {aiSuggestedDepts
                      .filter(d => !departments.some(existing => existing.name === d.name))
                      .map((dept, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => applyAiSuggestion(dept)}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-sky-200 bg-white hover:bg-sky-50 hover:border-sky-300 transition-all text-xs"
                          data-testid={`ai-dept-suggestion-${idx}`}
                        >
                          <span className="font-medium text-slate-800">{dept.name}</span>
                          <Badge variant="outline" className={`text-[9px] px-1 py-0 ${
                            dept.criticality === "critical" ? "bg-red-50 text-red-600 border-red-200" :
                            dept.criticality === "high" ? "bg-amber-50 text-amber-600 border-amber-200" :
                            "bg-slate-50 text-slate-500"
                          }`}>{dept.criticality}</Badge>
                          {dept.suggested_rto && <span className="text-[9px] text-slate-400">RTO: {dept.suggested_rto}</span>}
                        </button>
                      ))}
                    {aiSuggestedDepts.filter(d => !departments.some(existing => existing.name === d.name)).length === 0 && (
                      <span className="text-xs text-slate-500 italic">All suggested departments already added.</span>
                    )}
                  </div>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="dept-name">Department Name *</Label>
                <Input
                  id="dept-name"
                  value={deptForm.name}
                  onChange={(e) => setDeptForm({ ...deptForm, name: e.target.value })}
                  placeholder="e.g., IT Operations, Finance, HR"
                  data-testid="dept-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Criticality Level</Label>
                <Select value={deptForm.criticality} onValueChange={(v) => setDeptForm({ ...deptForm, criticality: v })}>
                  <SelectTrigger data-testid="dept-criticality-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High - Critical</SelectItem>
                    <SelectItem value="medium">Medium - Important</SelectItem>
                    <SelectItem value="low">Low - Support</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="dept-desc">Description (Optional)</Label>
              <Input
                id="dept-desc"
                value={deptForm.description}
                onChange={(e) => setDeptForm({ ...deptForm, description: e.target.value })}
                placeholder="Brief description"
                data-testid="dept-desc-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-slate-500" />
                Location(s) <span className="text-xs text-slate-400 ml-1">(Select multiple)</span>
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between font-normal"
                    data-testid="dept-location-multiselect"
                  >
                    <span className="truncate text-left">
                      {(() => {
                        const names = (deptForm.location_ids || [])
                          .map(id => locations.find(l => l.id === id)?.name).filter(Boolean);
                        if (deptForm.work_from_home) names.push("Work From Home");
                        return names.length ? names.join(", ") : "Select locations…";
                      })()}
                    </span>
                    <ChevronDown className="w-4 h-4 opacity-60 shrink-0 ml-2" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-2" align="start">
                  <div className="max-h-64 overflow-y-auto space-y-1">
                    {activeLocations.length === 0 && (
                      <p className="text-slate-400 italic text-xs px-2 py-1">No sites saved yet. Add sites in Org Locations & Setup.</p>
                    )}
                    {activeLocations.map((loc) => {
                      const checked = (deptForm.location_ids || []).includes(loc.id);
                      return (
                        <label key={loc.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer"
                          data-testid={`dept-location-option-${loc.id}`}>
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(v) => {
                              const cur = deptForm.location_ids || [];
                              setDeptForm({ ...deptForm, location_ids: v ? [...cur, loc.id] : cur.filter(id => id !== loc.id) });
                            }}
                          />
                          <span className="text-sm text-slate-700">{loc.name}{loc.city ? ` (${loc.city})` : ""}</span>
                        </label>
                      );
                    })}
                    <div className="border-t my-1" />
                    <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-amber-50 cursor-pointer" data-testid="dept-location-option-wfh">
                      <Checkbox
                        checked={!!deptForm.work_from_home}
                        onCheckedChange={(v) => setDeptForm({ ...deptForm, work_from_home: !!v })}
                      />
                      <span className="text-sm text-slate-700 flex items-center gap-1"><Home className="w-3.5 h-3.5 text-amber-600" /> Work From Home</span>
                    </label>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium text-sm text-slate-700 mb-3">Department Head</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Primary Department Head *</Label>
                    <button type="button"
                      className="text-xs text-sky-700 hover:underline"
                      data-testid="dept-primary-toggle-new"
                      onClick={() => setDeptForm({ ...deptForm,
                        primary_owner_mode: deptForm.primary_owner_mode === "new" ? "existing" : "new",
                        primary_owner_dirty: true, primary_owner_pending: "",
                        primary_owner_id: "" })}>
                      {deptForm.primary_owner_mode === "new" ? "Select existing" : "+ New person"}
                    </button>
                  </div>
                  {deptForm.primary_owner_pending && deptForm.primary_owner_mode === "existing" && !deptForm.primary_owner_id && (
                    <p className="text-xs text-amber-700" data-testid="dept-primary-pending-indicator">{deptForm.primary_owner_pending} — invite pending</p>
                  )}
                  {deptForm.primary_owner_mode === "new" ? (
                    <div className="grid grid-cols-2 gap-2" data-testid="dept-primary-new-fields">
                      <Input placeholder="First name" value={deptForm.primary_new.first_name}
                        onChange={(e) => setDeptForm({ ...deptForm, primary_new: { ...deptForm.primary_new, first_name: e.target.value } })} data-testid="dept-primary-new-first" />
                      <Input placeholder="Last name" value={deptForm.primary_new.last_name}
                        onChange={(e) => setDeptForm({ ...deptForm, primary_new: { ...deptForm.primary_new, last_name: e.target.value } })} data-testid="dept-primary-new-last" />
                      <Input className="col-span-2" placeholder="Email" type="email" value={deptForm.primary_new.email}
                        onChange={(e) => setDeptForm({ ...deptForm, primary_new: { ...deptForm.primary_new, email: e.target.value } })} data-testid="dept-primary-new-email" />
                    </div>
                  ) : (
                    <Select value={deptForm.primary_owner_id || (deptForm.primary_owner_pending ? "__pending__" : "none")} onValueChange={(v) => setDeptForm({ ...deptForm, primary_owner_id: (v === "none" || v === "__pending__") ? "" : v, primary_owner_dirty: v !== "__pending__", primary_owner_pending: v === "__pending__" ? deptForm.primary_owner_pending : "" })}>
                      <SelectTrigger data-testid="dept-primary-owner-select">
                        <SelectValue placeholder="Select Department Head" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Not Assigned —</SelectItem>
                        {deptForm.primary_owner_pending && !deptForm.primary_owner_id && (
                          <SelectItem value="__pending__" data-testid="dept-primary-pending-option">{deptForm.primary_owner_pending} — invite pending</SelectItem>
                        )}
                        {users.map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.first_name} {u.last_name} ({u.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Primary Contact</Label>
                  <Input
                    value={deptForm.primary_contact}
                    onChange={(e) => setDeptForm({ ...deptForm, primary_contact: e.target.value, primary_owner_dirty: true })}
                    placeholder="Phone number"
                    data-testid="dept-primary-contact"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-3">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Backup Department Head</Label>
                    <button type="button"
                      className="text-xs text-sky-700 hover:underline"
                      data-testid="dept-secondary-toggle-new"
                      onClick={() => setDeptForm({ ...deptForm,
                        secondary_owner_mode: deptForm.secondary_owner_mode === "new" ? "existing" : "new",
                        secondary_owner_dirty: true, secondary_owner_pending: "",
                        secondary_owner_id: "" })}>
                      {deptForm.secondary_owner_mode === "new" ? "Select existing" : "+ New person"}
                    </button>
                  </div>
                  {deptForm.secondary_owner_pending && deptForm.secondary_owner_mode === "existing" && !deptForm.secondary_owner_id && (
                    <p className="text-xs text-amber-700" data-testid="dept-secondary-pending-indicator">{deptForm.secondary_owner_pending} — invite pending</p>
                  )}
                  {deptForm.secondary_owner_mode === "new" ? (
                    <div className="grid grid-cols-2 gap-2" data-testid="dept-secondary-new-fields">
                      <Input placeholder="First name" value={deptForm.secondary_new.first_name}
                        onChange={(e) => setDeptForm({ ...deptForm, secondary_new: { ...deptForm.secondary_new, first_name: e.target.value } })} data-testid="dept-secondary-new-first" />
                      <Input placeholder="Last name" value={deptForm.secondary_new.last_name}
                        onChange={(e) => setDeptForm({ ...deptForm, secondary_new: { ...deptForm.secondary_new, last_name: e.target.value } })} data-testid="dept-secondary-new-last" />
                      <Input className="col-span-2" placeholder="Email" type="email" value={deptForm.secondary_new.email}
                        onChange={(e) => setDeptForm({ ...deptForm, secondary_new: { ...deptForm.secondary_new, email: e.target.value } })} data-testid="dept-secondary-new-email" />
                    </div>
                  ) : (
                    <Select value={deptForm.secondary_owner_id || (deptForm.secondary_owner_pending ? "__pending__" : "none")} onValueChange={(v) => setDeptForm({ ...deptForm, secondary_owner_id: (v === "none" || v === "__pending__") ? "" : v, secondary_owner_dirty: v !== "__pending__", secondary_owner_pending: v === "__pending__" ? deptForm.secondary_owner_pending : "" })}>
                      <SelectTrigger data-testid="dept-secondary-owner-select">
                        <SelectValue placeholder="Select Backup Department Head" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Not Assigned —</SelectItem>
                        {deptForm.secondary_owner_pending && !deptForm.secondary_owner_id && (
                          <SelectItem value="__pending__" data-testid="dept-secondary-pending-option">{deptForm.secondary_owner_pending} — invite pending</SelectItem>
                        )}
                        {users.map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.first_name} {u.last_name} ({u.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Secondary Contact</Label>
                  <Input
                    value={deptForm.secondary_contact}
                    onChange={(e) => setDeptForm({ ...deptForm, secondary_contact: e.target.value, secondary_owner_dirty: true })}
                    placeholder="Phone number"
                    data-testid="dept-secondary-contact"
                  />
                </div>
              </div>
              {/* B5 — owner phone + work location (+ WFH home address). Home is private. */}
              {["primary", "secondary"].map((pfx) => (
                (deptForm[`${pfx}_owner_id`] || (deptForm[`${pfx}_owner_mode`] === "new" && deptForm[`${pfx}_new`].email)) ? (
                  <div key={pfx} className="grid grid-cols-2 gap-4 mt-3 border-t pt-3" data-testid={`dept-${pfx}-contact-block`}>
                    <div className="space-y-2">
                      <Label>{pfx === "primary" ? "Primary Department Head" : "Backup Department Head"} — Phone</Label>
                      <Input value={deptForm[`${pfx}_owner_phone`]}
                        onChange={(e) => setDeptForm({ ...deptForm, [`${pfx}_owner_phone`]: e.target.value })}
                        placeholder="Personal/work phone" data-testid={`dept-${pfx}-owner-phone`} />
                    </div>
                    <div className="space-y-2">
                      <Label>{pfx === "primary" ? "Primary Department Head" : "Backup Department Head"} — Work Location</Label>
                      <Select value={deptForm[`${pfx}_owner_work_location`] || "none"}
                        onValueChange={(v) => setDeptForm({ ...deptForm, [`${pfx}_owner_work_location`]: v === "none" ? "" : v })}>
                        <SelectTrigger data-testid={`dept-${pfx}-owner-worklocation`}><SelectValue placeholder="Select site or WFH" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— Not set —</SelectItem>
                          {activeLocations.map((l) => <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>)}
                          <SelectItem value="work_from_home">Work From Home</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {(deptForm[`${pfx}_owner_work_location`] || "").toLowerCase() === "work_from_home" && (
                      <div className="col-span-2 grid grid-cols-2 gap-2 bg-amber-50 border border-amber-200 rounded p-2" data-testid={`dept-${pfx}-home-address`}>
                        <p className="col-span-2 text-[11px] text-amber-700">Home address is private — visible only to your organization's BCM Manager.</p>
                        <Input placeholder="Street" value={deptForm[`${pfx}_owner_home`].home_street}
                          onChange={(e) => setDeptForm({ ...deptForm, [`${pfx}_owner_home`]: { ...deptForm[`${pfx}_owner_home`], home_street: e.target.value } })} data-testid={`dept-${pfx}-home-street`} />
                        <Input placeholder="City" value={deptForm[`${pfx}_owner_home`].home_city}
                          onChange={(e) => setDeptForm({ ...deptForm, [`${pfx}_owner_home`]: { ...deptForm[`${pfx}_owner_home`], home_city: e.target.value } })} data-testid={`dept-${pfx}-home-city`} />
                        <Input placeholder="State / Province" value={deptForm[`${pfx}_owner_home`].home_state}
                          onChange={(e) => setDeptForm({ ...deptForm, [`${pfx}_owner_home`]: { ...deptForm[`${pfx}_owner_home`], home_state: e.target.value } })} data-testid={`dept-${pfx}-home-state`} />
                        <Input placeholder="Postal code" value={deptForm[`${pfx}_owner_home`].home_postal_code}
                          onChange={(e) => setDeptForm({ ...deptForm, [`${pfx}_owner_home`]: { ...deptForm[`${pfx}_owner_home`], home_postal_code: e.target.value } })} data-testid={`dept-${pfx}-home-postal`} />
                        <Input placeholder="Country (required)" value={deptForm[`${pfx}_owner_home`].home_country}
                          onChange={(e) => setDeptForm({ ...deptForm, [`${pfx}_owner_home`]: { ...deptForm[`${pfx}_owner_home`], home_country: e.target.value } })} data-testid={`dept-${pfx}-home-country`} />
                      </div>
                    )}
                  </div>
                ) : null
              ))}
            </div>
            {deptError && (
              <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 flex items-start gap-2" data-testid="dept-error-banner">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{deptError}</span>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowDeptDialog(false)}>Cancel</Button>
              <Button variant="outline" onClick={saveAndInviteHeads} disabled={!deptForm.name || invitingHeads}
                className="border-sky-300 text-sky-700 hover:bg-sky-50" data-testid="invite-dept-heads-btn">
                {invitingHeads ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                Invite Department Head(s)
              </Button>
              <Button onClick={saveDepartment} disabled={!deptForm.name} data-testid="save-dept-btn">
                {editingDept ? 'Update' : 'Create'} Department
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Executive Dialog */}
      <Dialog open={showExecDialog} onOpenChange={setShowExecDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingExec ? 'Edit Executive' : 'Add Executive'}</DialogTitle>
            <DialogDescription>
              {editingExec ? 'Update executive details' : 'Add a key executive, decision maker, or sponsor'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={execForm.name}
                  onChange={(e) => setExecForm({ ...execForm, name: e.target.value })}
                  placeholder="Full name"
                  data-testid="exec-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Designation *</Label>
                <Input
                  value={execForm.designation}
                  onChange={(e) => setExecForm({ ...execForm, designation: e.target.value })}
                  placeholder="e.g., CEO, CFO, CIO"
                  data-testid="exec-designation-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Role Type</Label>
                <Select 
                  value={['executive', 'decision_maker', 'sponsor'].includes(execForm.role_type) ? execForm.role_type : 'custom'} 
                  onValueChange={(v) => {
                    if (v !== 'custom') {
                      setExecForm({ ...execForm, role_type: v });
                    }
                  }}
                >
                  <SelectTrigger data-testid="exec-role-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="executive">Executive - C-Suite / Senior Leadership</SelectItem>
                    <SelectItem value="decision_maker">Decision Maker - BCM Decisions</SelectItem>
                    <SelectItem value="sponsor">Sponsor - Program Sponsor / Budget Approver</SelectItem>
                    <SelectItem value="custom">Custom Role Type</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Custom Role Type {!['executive', 'decision_maker', 'sponsor'].includes(execForm.role_type) && <span className="text-red-500">*</span>}</Label>
                <Input
                  value={!['executive', 'decision_maker', 'sponsor'].includes(execForm.role_type) ? execForm.role_type : ''}
                  onChange={(e) => setExecForm({ ...execForm, role_type: e.target.value })}
                  placeholder="Enter custom role type..."
                  disabled={['executive', 'decision_maker', 'sponsor'].includes(execForm.role_type)}
                  data-testid="exec-custom-role-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-slate-500" />
                Location
              </Label>
              <Select 
                value={execForm.location_id || "none"} 
                onValueChange={(v) => setExecForm({ ...execForm, location_id: v === "none" ? "" : v })}
              >
                <SelectTrigger data-testid="exec-location-select">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— No Location —</SelectItem>
                  {activeLocations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.name} {loc.city && `(${loc.city})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={execForm.email}
                  onChange={(e) => setExecForm({ ...execForm, email: e.target.value })}
                  placeholder="email@company.com"
                  data-testid="exec-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={execForm.phone}
                  onChange={(e) => setExecForm({ ...execForm, phone: e.target.value })}
                  placeholder="+1 (555) 123-4567"
                  data-testid="exec-phone-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Input
                value={execForm.notes}
                onChange={(e) => setExecForm({ ...execForm, notes: e.target.value })}
                placeholder="Additional notes about role or responsibilities"
                data-testid="exec-notes-input"
              />
            </div>
            <p className="text-xs text-slate-500">
              Note: Executives added here do not need to be registered users. Executives appear in your Stakeholder Register and can be assigned to BCM roles in the Governance Authority Matrix, which forms the crisis team. They do not sign in and are not assigned tasks.
            </p>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowExecDialog(false)}>Cancel</Button>
              <Button onClick={saveExecutive} disabled={!execForm.name || !execForm.designation || !execForm.role_type} data-testid="save-exec-btn">
                {editingExec ? 'Update' : 'Add'} Executive
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* IT-DRP Manager Invite Dialog */}
      <Dialog open={showITDRPInviteDialog} onOpenChange={setShowITDRPInviteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-indigo-600" />
              Invite IT-DRP Manager
            </DialogTitle>
            <DialogDescription>
              Send an invitation to the IT Disaster Recovery Plan Manager. They will receive an in-app notification and email.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInviteITDRP} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name <span className="text-red-500">*</span></Label>
                <Input
                  value={itdrpForm.first_name}
                  onChange={(e) => setItdrpForm({ ...itdrpForm, first_name: e.target.value })}
                  placeholder="First name"
                  required
                  data-testid="itdrp-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name <span className="text-red-500">*</span></Label>
                <Input
                  value={itdrpForm.last_name}
                  onChange={(e) => setItdrpForm({ ...itdrpForm, last_name: e.target.value })}
                  placeholder="Last name"
                  required
                  data-testid="itdrp-last-name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email <span className="text-red-500">*</span></Label>
              <Input
                type="email"
                value={itdrpForm.email}
                onChange={(e) => setItdrpForm({ ...itdrpForm, email: e.target.value })}
                placeholder="itdrp.manager@company.com"
                required
                data-testid="itdrp-email"
              />
            </div>
            <p className="text-xs text-slate-500">
              The IT-DRP Manager will receive an in-app notification. An invitation email with a sign-up link and a one-time code will be sent to this address.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowITDRPInviteDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={itdrpInviting || !itdrpForm.email || !itdrpForm.first_name} className="bg-indigo-600 hover:bg-indigo-700" data-testid="send-itdrp-invite-btn">
                {itdrpInviting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send Invitation
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* V14 — BCM-only Special Invite Dialog (Exec Sponsor / Auditor / Deputy) */}
      <Dialog open={showBcmExceptionDialog} onOpenChange={setShowBcmExceptionDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              BCM Special Invite
            </DialogTitle>
            <DialogDescription>
              BCM Managers can only invite these three roles directly. All other
              invitations must flow through a Department Head. Each invite is
              audit-logged for ISO 22301 traceability.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleBcmExceptionInvite} className="space-y-4">
            <div className="space-y-2">
              <Label>Role <span className="text-red-500">*</span></Label>
              <div className="grid grid-cols-1 gap-2">
                {[
                  {
                    value: "executive_sponsor",
                    label: "Executive Sponsor",
                    reason: "ISO 22301 §5.1 leadership commitment",
                    ring: "border-amber-300 bg-amber-50 text-amber-800",
                  },
                  {
                    value: "viewer",
                    label: "External Auditor / Viewer",
                    reason: "Read-only access for ISO auditors & consultants",
                    ring: "border-sky-300 bg-sky-50 text-sky-800",
                  },
                  {
                    value: "bcm_manager",
                    label: "Deputy BCM Manager",
                    reason: "Program continuity / backup authority",
                    ring: "border-purple-300 bg-purple-50 text-purple-800",
                  },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setBcmExceptionForm({ ...bcmExceptionForm, role: opt.value })}
                    className={`text-left p-3 rounded-lg border-2 transition-all ${
                      bcmExceptionForm.role === opt.value ? opt.ring : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                    data-testid={`bcm-exception-role-${opt.value}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold">{opt.label}</span>
                      {bcmExceptionForm.role === opt.value && (
                        <CheckCircle2 className="w-4 h-4" />
                      )}
                    </div>
                    <p className="text-xs mt-0.5 opacity-80">{opt.reason}</p>
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name <span className="text-red-500">*</span></Label>
                <Input
                  value={bcmExceptionForm.first_name}
                  onChange={(e) => setBcmExceptionForm({ ...bcmExceptionForm, first_name: e.target.value })}
                  placeholder="First name"
                  required
                  data-testid="bcm-exception-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name <span className="text-red-500">*</span></Label>
                <Input
                  value={bcmExceptionForm.last_name}
                  onChange={(e) => setBcmExceptionForm({ ...bcmExceptionForm, last_name: e.target.value })}
                  placeholder="Last name"
                  required
                  data-testid="bcm-exception-last-name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email <span className="text-red-500">*</span></Label>
              <Input
                type="email"
                value={bcmExceptionForm.email}
                onChange={(e) => setBcmExceptionForm({ ...bcmExceptionForm, email: e.target.value })}
                placeholder="name@company.com"
                required
                data-testid="bcm-exception-email"
              />
            </div>
            <p className="text-xs text-slate-500 flex items-start gap-1.5">
              <Shield className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
              Logged to the audit trail as <code className="mx-1 text-[0.7rem] bg-slate-100 px-1.5 py-0.5 rounded">bcm_manager.exception_invite</code> with the ISO-backed reason code.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowBcmExceptionDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={bcmExceptionInviting || !bcmExceptionForm.email || !bcmExceptionForm.first_name} className="bg-purple-600 hover:bg-purple-700" data-testid="send-bcm-exception-invite-btn">
                {bcmExceptionInviting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send Special Invite
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* V22.9 — Inline "Invite Department Head" (pre-fills department on the row) */}
      <RoleBasedInviteModal
        open={inlineInvite.open}
        onClose={() => setInlineInvite({ open: false, dept: null })}
        onInvited={() => { setInlineInvite({ open: false, dept: null }); fetchData?.(); }}
        defaultRole="department_head"
        defaultDepartmentId={inlineInvite.dept?.id || ""}
        defaultDepartmentName={inlineInvite.dept?.name || ""}
      />
      {/* V22.9 — Bulk CSV invite */}
      <BulkInviteCSVModal
        open={showBulkInviteModal}
        onClose={() => setShowBulkInviteModal(false)}
        onInvited={() => fetchData?.()}
      />
    </div>
  );
}
